<template>
  <div class="omnisearch">
    <label class="text-white">{{
      t('contractor.omni_search_contractor.seach_by')
    }}</label>
    <div class="input-wrapper">
      <input
        type="text"
        class="form-control"
        :placeholder="t('contractor.omni_search_contractor.placeholder')"
        aria-label="Search"
        v-model="search"
        @input="handleSearch"
        @keydown.down.prevent="focusNext"
        @keydown.up.prevent="focusPrev"
        @keydown.enter.prevent="onEnter"
        @keydown.esc.prevent="handleClose"
        autofocus
        ref="searchInput"
      />
      <template v-if="search">
        <span
          class="material-symbols-outlined text-primary icon"
          @click="clearSearch"
        >
          close
        </span>
      </template>
      <template v-else>
        <span class="material-symbols-outlined text-primary icon">search</span>
      </template>
    </div>

    <ul
      class="form-control"
      v-if="search.length > 1"
    >
      <template v-if="loading">
        <li class="text-center text-small">
          <div><Spinner class="rotate" /></div>
        </li>
      </template>
      <template v-else-if="results?.length">
        <li
          v-for="(result, index) in results"
          :key="result.id"
          class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between"
          @click="openContractor(result)"
          :class="{ highlight: activeIndex === index }"
        >
          <div class="d-flex flex-column">
            <div class="d-flex mb-1">
              <h5 class="mb-0 me-2">
                {{ result.name }}
              </h5>
            </div>
            <div class="d-flex text-small">
              <div class="d-flex align-items-center me-2">
                <span class="material-symbols-outlined me-1 fs-1">
                  location_on
                </span>
                <span v-if="result.address">{{ result.address }}</span>
                <span v-else>{{
                  t(
                    'contractor.omni_search_contractor.no_primary_address_found'
                  )
                }}</span>
              </div>

              <div class="d-flex align-items-center me-2">
                <span class="material-symbols-outlined me-1 fs-1">
                  handyman
                </span>
                {{ result.primary_contact }}
              </div>
            </div>
          </div>
        </li>
      </template>
      <template v-else-if="search">
        <li class="text-center text-small">
          <div>
            {{
              t('contractor.omni_search_contractor.no_results_found_warning')
            }}
          </div>
          <div>
            {{
              t(
                'contractor.omni_search_contractor.search_by_another_param_warning'
              )
            }}
          </div>
        </li>
      </template>
    </ul>
  </div>
  <div
    class="omnisearch-backdrop"
    @click="handleClose"
  ></div>
</template>

<script setup lang="ts">
import Spinner from '@shared/components/Spinner.vue';
import {
  ContractorRoutesRepository,
  type ContractorSearchResponseType
} from '@shared/generated';
import Fuse from 'fuse.js';
import { nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n();

const emit = defineEmits(['close']);

const search = ref('');
const searchInput = ref<HTMLInputElement>();
const loading = ref(true);

onBeforeMount(() => {
  document.getElementsByTagName('body')[0].classList.add('scroll-disabled');
});

onBeforeUnmount(() => {
  document.getElementsByTagName('body')[0].classList.remove('scroll-disabled');
});

onMounted(async () => {
  // focus on next tick
  nextTick(() => {
    searchInput.value?.focus();
  });
  contractors.value =
    await ContractorRoutesRepository.contractorRoutesContractorSearchInputs({});

  loading.value = false;

  // if theres a search value from prior to api call, run search
  if (search.value) {
    handleSearch();
  }
});

const contractors = ref<ContractorSearchResponseType[]>();

const results = ref<ContractorSearchResponseType[]>();
const handleSearch = () => {
  if (!contractors.value || !search.value) return;

  const fuse = new Fuse(contractors.value, {
    minMatchCharLength: 2,
    threshold: 0.4,
    ignoreLocation: true,
    keys: [
      {
        name: 'primary_contact',
        weight: 2
      },
      'address',
      'city',
      'phone',
      'email',
      {
        name: 'contractor',
        weight: 4
      },
      { name: 'name', weight: 5 }
    ]
  });

  results.value = fuse.search(search.value).map((result) => result.item);
};

const activeIndex = ref(0);
const focusNext = () => {
  if (!results.value?.length) return;
  if (activeIndex.value === undefined) {
    activeIndex.value = 0;
  } else if (activeIndex.value < results.value.length - 1) {
    activeIndex.value++;
  }
};

const focusPrev = () => {
  if (activeIndex.value === undefined) {
    activeIndex.value = 0;
  } else if (activeIndex.value > 0) {
    activeIndex.value--;
  }
};

const onEnter = () => {
  if (
    activeIndex.value !== undefined &&
    results.value &&
    results.value[activeIndex.value]
  ) {
    openContractor(results.value[activeIndex.value]);
  }
};

const router = useRouter();
const openContractor = (contractor: ContractorSearchResponseType) => {
  router.push({
    name: 'ContractorDetail',
    params: { contractorId: contractor.id }
  });
  emit('close');
};

const clearSearch = () => {
  search.value = '';
  results.value = undefined;
};

const handleClose = () => {
  emit('close');
};
</script>

<style lang="scss" scoped>
$omnisearch-top-desktop: 200px;
$omnisearch-top-mobile: 100px;
$omnisearch-list-margin: 0.75rem;
$omnisearch-container-width: 700px;

// magic number to account for height of label + input
$omnisearch-input-height: 72px;

.omnisearch-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(113, 113, 111, 0.8);
  backdrop-filter: blur(3px);
  z-index: 10001; // 1 higher than header component
}
.omnisearch {
  position: fixed;
  top: $omnisearch-top-desktop;
  z-index: 10002; // 1 higher than backdrop
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: $omnisearch-container-width;
  @media screen and (max-width: 768px) {
    top: $omnisearch-top-mobile;
    max-width: calc(100vw - 1rem);
  }

  .input-wrapper {
    position: relative;
    .icon {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 1rem;
    }
  }

  ul {
    list-style: none;
    margin: $omnisearch-list-margin 0 0;
    padding: 0;
    max-height: calc(
      100vh -
        (
          #{$omnisearch-top-desktop} + #{$omnisearch-list-margin} + #{$omnisearch-input-height} +
            1rem
        )
    );
    overflow-y: auto;
    overflow-x: hidden;

    @media screen and (max-width: 768px) {
      max-height: calc(
        100vh -
          (
            #{$omnisearch-top-mobile} + #{$omnisearch-list-margin} + #{$omnisearch-input-height} +
              1rem
          )
      );
    }

    li {
      padding: 1rem;
      border-bottom: 1px solid #e0e0e0;
      cursor: pointer;
      &:last-child {
        border-bottom: none;
      }
      &:hover,
      &.highlight {
        background-color: #f5f5f5;
      }
    }
  }
}
</style>
